<template>
  <div class="station-editor" v-if="station">
    <!-- Export Started Banner -->
    <transition name="banner-slide">
      <div v-if="showExportBanner" class="export-banner">
        <div class="banner-content">
          <AppIcon name="check-circle" />
          <span>Export started! Your file is being processed.</span>
          <router-link :to="`/station/${slug}/exports`" class="banner-link">View Exports <AppIcon name="arrow-right" /></router-link>
        </div>
        <button class="banner-close" @click="dismissBanner"><AppIcon name="times" /></button>
      </div>
    </transition>
    
    <AppHeader :station="station" @goto-timestamp="gotoTimestamp" @go-live="onGoLive" @back="goBack" />
    <div class="timeline-wrapper">
      <Timeline ref="timeline" @seek="gotoTimestamp" @pause-on-empty="onPauseOnEmpty" />
      <transition name="fade">
        <div v-if="trackSearchMode" class="timeline-overlay">
          <span>Select a result to navigate</span>
        </div>
      </transition>
    </div>
    <ExportPanel v-if="hasSelection || hasExportCuts" @export-started="onExportStarted" />
    <Player />
    <TrackList ref="trackList" @search-mode-change="onSearchModeChange" />
    <audio id="audio-player" preload="none"></audio>
    <audio id="audio-preview" preload="none"></audio>
  </div>
  <div v-else-if="loading" class="loading-state">
    <p>Loading station...</p>
  </div>
  <div v-else class="error-state">
    <h2>Station Not Found</h2>
    <p>The station "{{ slug }}" does not exist.</p>
    <router-link to="/" class="btn-primary"><AppIcon name="arrow-left" />Back to Stations</router-link>
  </div>
</template>

<script>
import AppHeader from '../components/AppHeader.vue'
import Timeline from '../components/Timeline.vue'
import Player from '../components/Player.vue'
import TrackList from '../components/TrackList.vue'
import ExportPanel from '../components/ExportPanel.vue'
import websocket from '../services/websocket'
import audioController from '../services/audioController'

export default {
  name: 'StationEditor',
  components: { AppHeader, Timeline, Player, TrackList, ExportPanel },
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      station: null,
      loading: true,
      showExportBanner: false,
      bannerTimeout: null,
      trackSearchMode: false
    }
  },
  computed: {
    hasSelection() {
      return this.$store.state.selectionStart !== null && this.$store.state.selectionEnd !== null
    },
    hasExportCuts() {
      return this.$store.state.exportCuts && this.$store.state.exportCuts.length > 0
    }
  },
  watch: {
    // Watch for slug changes (when navigating between stations)
    slug: {
      handler(newSlug, oldSlug) {
        if (oldSlug && newSlug !== oldSlug) {
          // Navigating to a different station - full reinit
          audioController.reset()  // Reset MSE state before switching
          this.cleanup()
          this.$store.commit('RESET_STATE')
          this.initStation(newSlug)
        }
      }
    }
  },
  async created() {
    // Reset state when entering a station
    this.$store.commit('RESET_STATE')
    // Initial load
    await this.initStation(this.slug)
  },
  beforeUnmount() {
    this.cleanup()
    // Reset store state for next station
    this.$store.commit('RESET_STATE')
  },
  methods: {
    onSearchModeChange(isSearching) {
      this.trackSearchMode = isSearching
    },
    cleanup() {
      if (this.refreshInterval) clearInterval(this.refreshInterval)
      if (this.renderInterval) clearInterval(this.renderInterval)
      if (this.timeInterval) clearInterval(this.timeInterval)
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame)
      window.removeEventListener('keydown', this.handleKeydown)
      websocket.disconnect()
      audioController.destroy()
      this.refreshInterval = null
      this.renderInterval = null
      this.timeInterval = null
      this.animationFrame = null
    },
    async initStation(slug) {
      // Load station first
      await this.loadStation(slug)
      
      if (!this.station) return
      
      // Initialize audio controller after next tick when DOM is ready
      this.$nextTick(() => {
        audioController.init(this.$store)
      })
      
      // Connect websocket for this station
      websocket.connect(this.$store, slug)
      
      // Fetch initial data
      await Promise.all([
        this.fetchStatus(),
        this.$store.dispatch('fetchTimelineData'),
        this.fetchStats(),
        this.$store.dispatch('fetchMetadataPageAction', {})
      ])
      
      // Set active metadata to most recent on first load
      if (this.$store.state.metadataList.length > 0) {
        this.$store.commit('SET_ACTIVE_METADATA_ID', this.$store.state.metadataList[0].id)
      }
      
      // Scroll to active after initial load
      this.$nextTick(() => {
        if (this.$refs.trackList) {
          this.$refs.trackList.scrollToActive()
        }
      })
      
      // Start refresh intervals
      // NOTE: Commented out for testing - WebSocket handles real-time updates
      // Uncomment if issues arise with data sync
      // this.refreshInterval = setInterval(() => {
      //   this.$store.dispatch('fetchTimelineData')
      //   this.fetchStats()
      // }, 10000)
      
      // Time display update - updates current time when live
      // This triggers view updates via SET_CURRENT_TIME watchers
      this.timeInterval = setInterval(() => {
        if (this.$store.state.isLive) {
          this.$store.commit('SET_CURRENT_TIME', Date.now())
        }
      }, 200)
      
      // Keyboard shortcuts
      window.addEventListener('keydown', this.handleKeydown)
    },
    async loadStation(slug) {
      this.loading = true
      this.station = null
      
      try {
        const { fetchStationBySlug } = await import('../services/api')
        this.station = await fetchStationBySlug(slug)
        if (this.station) {
          // Set station in store
          this.$store.commit('SET_STATION', this.station)
        }
      } catch (err) {
        console.error('Error loading station:', err)
      } finally {
        this.loading = false
      }
    },
    async fetchStatus() {
      try {
        const { fetchStatus } = await import('../services/api')
        const data = await fetchStatus()
        this.$store.commit('SET_SOURCE_CONNECTED', data.sourceConnected)
      } catch (err) {
        console.error('[StationEditor] fetchStatus error:', err)
      }
    },
    async fetchStats() {
      try {
        const { fetchStats } = await import('../services/api')
        await fetchStats()
      } catch (err) {
        // Stats come from segments
      }
    },
    gotoTimestamp(timestamp) {
      this.$store.dispatch('gotoTimestamp', timestamp)
    },
    onPauseOnEmpty(timestamp) {
      // Clicked on empty area - pause playback and update position
      const audio = document.getElementById('audio-player')
      if (audio) audio.pause()
      
      this.$store.commit('SET_LIVE', false)
      this.$store.commit('SET_VIEW_LOCKED', false)
      this.$store.commit('SET_CURRENT_TIME', timestamp)
      this.$store.commit('SET_CURRENT_SEGMENT_ID', null)
    },
    onGoLive() {
      this.$nextTick(() => {
        if (this.$refs.trackList) {
          this.$refs.trackList.scrollToActive()
        }
      })
    },
    goBack() {
      this.$router.back()
    },
    handleKeydown(e) {
      if (e.target.tagName === 'INPUT') return
      switch (e.key) {
        case ' ': e.preventDefault(); audioController.toggle(); break
        case 'ArrowLeft': e.preventDefault(); audioController.skip(-10); break
        case 'ArrowRight': e.preventDefault(); audioController.skip(10); break
        case 'l': case 'L': this.$store.dispatch('goLive'); break
        case 'm': case 'M': audioController.toggleMute(); break
        case 'Escape': 
          if (this.$store.state.selectionMode) {
            this.$store.dispatch('clearSelection')
          }
          break
        case '[': e.preventDefault(); this.$store.dispatch('nudgeSelection', { target: 'start', delta: -1000 }); break
        case ']': e.preventDefault(); this.$store.dispatch('nudgeSelection', { target: 'end', delta: 1000 }); break
        case '{': e.preventDefault(); this.$store.dispatch('nudgeSelection', { target: 'start', delta: 1000 }); break
        case '}': e.preventDefault(); this.$store.dispatch('nudgeSelection', { target: 'end', delta: -1000 }); break
      }
    },
    onExportStarted() {
      // Clear any existing timeout
      if (this.bannerTimeout) {
        clearTimeout(this.bannerTimeout)
      }
      
      // Show banner
      this.showExportBanner = true
      
      // Auto-dismiss after 5 seconds
      this.bannerTimeout = setTimeout(() => {
        this.showExportBanner = false
      }, 5000)
    },
    dismissBanner() {
      this.showExportBanner = false
      if (this.bannerTimeout) {
        clearTimeout(this.bannerTimeout)
        this.bannerTimeout = null
      }
    }
  }
}
</script>

<style scoped>
.station-editor {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 74px);
  overflow: hidden;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 74px);
  text-align: center;
  color: var(--gray-500);
}

.error-state h2 {
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-700);
  margin-bottom: 8px;
}

.error-state p {
  margin-bottom: 20px;
}

.btn-primary {
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  background: var(--accent);
  color: var(--white);
  cursor: pointer;
  text-decoration: none;
}

.btn-primary:hover {
  background: var(--accent-hover);
}

/* Export Banner */
.export-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  box-shadow: var(--shadow-md);
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 500;
}

.banner-content i.fa-check-circle {
  font-size: 18px;
}

.banner-link {
  color: white;
  text-decoration: none;
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 14px;
  border-radius: 4px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.banner-link:hover {
  background: rgba(255, 255, 255, 0.3);
}

.banner-link i {
  font-size: 11px;
}

.banner-close {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  opacity: 0.8;
  font-size: 16px;
}

.banner-close:hover {
  opacity: 1;
}

/* Banner animation */
.banner-slide-enter-active,
.banner-slide-leave-active {
  transition: all 0.3s ease;
}

.banner-slide-enter,
.banner-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

/* Timeline wrapper for overlay positioning */
.timeline-wrapper {
  position: relative;
}

.timeline-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: all;
}

.timeline-overlay span {
  font-size: 13px;
  color: var(--gray-500);
  font-weight: 500;
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
